import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";
import { buildRedirectUrls, PC_STATE } from "./constants";

const ACCOUNT_URL = `${process.env.REACT_APP_API_URL}/payments/collect/account/`;
const ONBOARDING_LINKS_URL = `${ACCOUNT_URL}onboarding-links/`;
const LOGIN_LINKS_URL = `${ACCOUNT_URL}login-links/`;
const REMOVAL_CHECK_URL = `${ACCOUNT_URL}removal-check/`;

// DELETE /account/ 409 codes: removal is blocked, nothing was deleted.
const REMOVAL_BLOCKED_CODES = [
  "payment_collect.balance_pending",
  "payment_collect.open_disputes",
  "payment_collect.payouts_in_transit",
];

const deriveState = (account) => {
  if (!account) return PC_STATE.NOT_CONNECTED;
  if (account.status === "pending") {
    return account.details_submitted ? PC_STATE.PENDING : PC_STATE.INCOMPLETE;
  }
  return account.status;
};

export const fetchConnectAccount = createAsyncThunk(
  "paymentConnection/fetch",
  async (_, { rejectWithValue }) => {
    try {
      return await request(ACCOUNT_URL, { method: "GET" });
    } catch (error) {
      if (error?.status === 404) {
        return rejectWithValue({ notConnected: true });
      }
      // Only HasCompanyConnectOwnership returns 403 here, and it checks one
      // thing: the user has a Company. Pair the status with the DRF error code
      // so an infrastructure-layer 403 cannot match by accident.
      if (
        error?.status === 403 &&
        error?.error_code === "auth.permission_denied"
      ) {
        return rejectWithValue({ noCompany: true });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Failed to load payment status",
      });
    }
  }
);

export const createConnectAccount = createAsyncThunk(
  "paymentConnection/create",
  async (_, { rejectWithValue }) => {
    try {
      return await request(ACCOUNT_URL, {
        method: "POST",
        body: JSON.stringify(buildRedirectUrls()),
      });
    } catch (error) {
      if (error?.status === 403) {
        return rejectWithValue({ subscriptionRequired: true });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't start setup",
        code: error?.error_code,
      });
    }
  }
);

export const createOnboardingLink = createAsyncThunk(
  "paymentConnection/onboardingLink",
  async (_, { rejectWithValue }) => {
    try {
      return await request(ONBOARDING_LINKS_URL, {
        method: "POST",
        body: JSON.stringify(buildRedirectUrls()),
      });
    } catch (error) {
      if (error?.status === 403) {
        return rejectWithValue({ subscriptionRequired: true });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't resume setup",
        code: error?.error_code,
      });
    }
  }
);

export const createLoginLink = createAsyncThunk(
  "paymentConnection/loginLink",
  async (_, { rejectWithValue }) => {
    try {
      // Security: this url is single-use — never persist or log it.
      const data = await request(LOGIN_LINKS_URL, { method: "POST" });
      return data?.url;
    } catch (error) {
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't open Stripe dashboard",
        code: error?.error_code,
      });
    }
  }
);

export const setAccountDisabled = createAsyncThunk(
  "paymentConnection/setDisabled",
  async (disabled, { rejectWithValue }) => {
    try {
      return await request(ACCOUNT_URL, {
        method: "PATCH",
        body: JSON.stringify({ disabled }),
      });
    } catch (error) {
      return rejectWithValue({
        message: error?.user_error_msg || "Update failed",
        code: error?.error_code,
      });
    }
  }
);

export const checkRemoval = createAsyncThunk(
  "paymentConnection/removalCheck",
  async (_, { rejectWithValue }) => {
    try {
      return await request(REMOVAL_CHECK_URL, { method: "GET" });
    } catch (error) {
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't check payment setup",
        code: error?.error_code,
      });
    }
  }
);

export const removeConnectAccount = createAsyncThunk(
  "paymentConnection/remove",
  async (_, { rejectWithValue }) => {
    try {
      await request(ACCOUNT_URL, { method: "DELETE" });
      return true;
    } catch (error) {
      if (
        error?.status === 409 &&
        REMOVAL_BLOCKED_CODES.includes(error?.error_code)
      ) {
        return rejectWithValue({
          removalBlocked: true,
          message: error.user_error_msg,
        });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't remove setup",
        code: error?.error_code,
      });
    }
  }
);

const initialState = {
  account: null,
  derivedState: PC_STATE.LOADING,
  fetchStatus: "idle",
  actionStatus: "idle",
  actionError: null,
};

const paymentConnectionSlice = createSlice({
  name: "paymentConnection",
  initialState,
  reducers: {
    clearActionStatus: (state) => {
      state.actionStatus = "idle";
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectAccount.pending, (state) => {
        state.fetchStatus = "loading";
      })
      .addCase(fetchConnectAccount.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        state.account = action.payload;
        state.derivedState = deriveState(action.payload);
      })
      .addCase(fetchConnectAccount.rejected, (state, action) => {
        state.fetchStatus = "failed";
        if (action.payload?.notConnected) {
          state.account = null;
          state.derivedState = PC_STATE.NOT_CONNECTED;
        } else if (action.payload?.noCompany) {
          state.account = null;
          state.derivedState = PC_STATE.NO_COMPANY;
        } else {
          state.derivedState = PC_STATE.ERROR_FETCH;
          state.actionError = action.payload;
        }
      });

    [
      createConnectAccount,
      createOnboardingLink,
      createLoginLink,
      setAccountDisabled,
      checkRemoval,
      removeConnectAccount,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.actionStatus = "loading";
          state.actionError = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.actionStatus = "failed";
          state.actionError = action.payload;
        });
    });

    builder
      .addCase(setAccountDisabled.fulfilled, (state, action) => {
        state.actionStatus = "success";
        state.account = action.payload;
        state.derivedState = deriveState(action.payload);
      })
      .addCase(removeConnectAccount.fulfilled, (state) => {
        state.actionStatus = "success";
        state.account = null;
        state.derivedState = PC_STATE.NOT_CONNECTED;
      })
      .addCase(createConnectAccount.fulfilled, (state) => {
        state.actionStatus = "success";
      })
      .addCase(createOnboardingLink.fulfilled, (state) => {
        state.actionStatus = "success";
      })
      .addCase(checkRemoval.fulfilled, (state) => {
        state.actionStatus = "success";
      })
      .addCase(createLoginLink.fulfilled, (state) => {
        state.actionStatus = "success";
      });
  },
});

export const { clearActionStatus } = paymentConnectionSlice.actions;
export default paymentConnectionSlice.reducer;
